import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {WEBSOCKET_URL} from "../components/constants.js";

const WebSocketContext = createContext(null);

export const useWebSocket = () => useContext(WebSocketContext);

export const WebSocketProvider = ({ children }) => {
    const [isConnected, setIsConnected] = useState(false);
    // Add default time values to initial state
    const [worldState, setWorldState] = useState({
        players: {},
        plants: [],
        walls: [], // Add walls if you expect them
        tree: null, // Add tree if you expect it
        dayLengthMinutes: 15, // Default day length
        initialTimeOfDay: 0.3, // Default start time
    });
    const [resumedPlayerState, setResumedPlayerState] = useState(null); // NEW: resume
    const [diamonds, setDiamonds] = useState({}); // NEW: diamonds map by id
    // NEW: transient pickup effects list
    const [pickupEffects, setPickupEffects] = useState([]);
    const [myScore, setMyScore] = useState(0); // NEW: my total diamonds collected
    const [myGlow, setMyGlow] = useState({ level: 0, updatedMs: 0 }); // NEW: my glow track
    const ws = useRef(null);

    // Generate or retrieve player UUID
    const [pUUID, setPUUID] = useState(() => {
        // Try to get from localStorage first
        const savedUUID = localStorage.getItem('pUUID');
        if (savedUUID && savedUUID !== 'null' && savedUUID !== 'undefined') {
            return savedUUID;
        }

        // Generate new UUID if none exists
        const newUUID = uuidv4();
        try {
            localStorage.setItem('pUUID', newUUID);
        } catch (error) {
            console.error("Failed to save UUID to localStorage:", error);
        }
        return newUUID;
    });

    const [serverTimeOffset, setServerTimeOffset] = useState(0);
    const [avgPingTime, setAvgPingTime] = useState(100); // Default estimate (ms)
    const pingHistory = useRef([]);

    // Create refs to track the latest values without re-rendering
    const statsRef = useRef({
        avgPingTime: 100,
        serverTimeOffset: 0
    });

    // Update the ref whenever the actual values change
    useEffect(() => {
        statsRef.current.avgPingTime = avgPingTime;
        statsRef.current.serverTimeOffset = serverTimeOffset;
    }, [avgPingTime, serverTimeOffset]);

    // Function to get estimated server time
    const getServerTime = useCallback(() => {
        return Date.now() + serverTimeOffset;
    }, [serverTimeOffset]);

    const connectWebSocket = useCallback(() => {
        console.log('Attempting to connect WebSocket...');
        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
            console.log('WebSocket already connected.');
            setIsConnected(true);
            return; // Already connected
        }

        const newWsConnection = new WebSocket(WEBSOCKET_URL);

        newWsConnection.onopen = () => {
            console.log('WebSocket Connected');
            setIsConnected(true);
            // Identify to receive resume state if available
            try {
                const hello = JSON.stringify({ type: 'hello', pUUID, timestamp: Date.now() });
                newWsConnection.send(hello);
            } catch (e) {
                console.error('Failed to send hello:', e);
            }
        };

        newWsConnection.onclose = (event) => {
            console.log('WebSocket Disconnected:', event.reason, event.code);
            console.log("WebSocketContext: Setting isConnected to false."); // Verify this call happens
            setIsConnected(false);

            // Only set ws.current to null AFTER we've updated isConnected
            setTimeout(() => {
                if (ws.current === newWsConnection) {
                    ws.current = null;
                }

                // Attempt reconnection after delay
                setTimeout(connectWebSocket, 5000);
            }, 0);
        };

        // Set the ref to the new connection BEFORE any events might fire
        ws.current = newWsConnection;

        ws.current.onerror = (error) => {
            console.error('WebSocket Error:', error);
        };

        ws.current.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data);
                // --- Process different message types ---
                switch (message.type) {
                    case 'pong':
                        // Calculate round-trip time
                        const now = Date.now();
                        const sentTime = message.timestamp; // When we sent the ping
                        const serverTime = message.serverTime; // When server received it

                        const rtt = now - sentTime; // Round-trip time

                        // Update ping history (keep last 5)
                        pingHistory.current.push(rtt);
                        if (pingHistory.current.length > 5) {
                            pingHistory.current.shift();
                        }

                        // Calculate average ping
                        const avgPing = pingHistory.current.reduce((sum, ping) => sum + ping, 0) /
                            pingHistory.current.length;
                        setAvgPingTime(avgPing);

                        // Estimate one-way trip time
                        const oneWayTime = avgPing / 2;

                        // Estimate offset between client and server clocks
                        // client_time + offset = server_time
                        // Therefore: offset = server_time - client_time
                        const estimatedServerTimeAtReceive = serverTime + oneWayTime;
                        const clientTimeAtReceive = sentTime + rtt/2;
                        const offset = estimatedServerTimeAtReceive - clientTimeAtReceive;

                        // Update offset with some smoothing
                        setServerTimeOffset(prev => {
                            if (prev === 0) return offset;
                            return prev * 0.8 + offset * 0.2; // 80% old, 20% new
                        });
                        break;
                    case 'fullWorldState':
                        console.log("Received fullWorldState:", message.payload); // Log to verify time fields
                        // Assuming payload structure matches our state:
                        // { players: {...}, plants: [...], walls: [...], tree: {...}, dayLengthMinutes: N, initialTimeOfDay: N }
                        setWorldState(message.payload);
                        break;
                    case 'playerSnapshot':
                        console.log("Received full player snapshot");

                        // When receiving a full snapshot, we should treat it as authoritative
                        // This handles both updates and removals of players
                        setWorldState(prevState => ({
                            ...prevState,
                            players: message.payload.players || {},
                        }));
                        // If our pUUID exists, update myScore/myGlow from server
                        if (message.payload?.players && message.payload.players[pUUID]) {
                            const me = message.payload.players[pUUID];
                            if (typeof me.score === 'number') setMyScore(me.score);
                            if (typeof me.glowLevel === 'number') setMyGlow({ level: me.glowLevel || 0, updatedMs: me.glowUpdatedMs || Date.now() });
                        }
                        break;
                    case 'playerUpdates': // note the plural
                        console.log("Received player updates:", Object.keys(message.payload.updates || {}).length);
                        const updates = message.payload?.updates || {};
                        setWorldState(prevState => {
                            const newPlayers = { ...prevState.players };
                            Object.entries(updates).forEach(([id, state]) => {
                                newPlayers[id] = state;
                            });
                            return { ...prevState, players: newPlayers };
                        });
                        // If our own update is included, refresh myScore/myGlow
                        if (updates[pUUID]) {
                            const me = updates[pUUID];
                            if (typeof me.score === 'number') setMyScore(me.score);
                            if (typeof me.glowLevel === 'number') setMyGlow({ level: me.glowLevel || 0, updatedMs: me.glowUpdatedMs || Date.now() });
                        }
                        break;
                    case 'playerUpdate':
                        // this is a single player update (not using this currently)
                        setWorldState(prevState => ({
                            ...prevState,
                            players: {
                                ...prevState.players,
                                [message.payload.pUUID]: message.payload.state,
                            },
                        }));
                        break;
                    case 'playerJoined':
                         // No change needed here
                         setWorldState(prevState => ({
                            ...prevState,
                            players: {
                                ...prevState.players,
                                [message.payload.pUUID]: message.payload.initialState,
                            },
                        }));
                        break;
                    case 'playerLeft':
                        console.log(`Player ${message.payload.pUUID} left the game`);
                        setWorldState(prevState => {
                            const newPlayers = { ...prevState.players };
                            delete newPlayers[message.payload.pUUID];
                            return { ...prevState, players: newPlayers };
                        });
                        break;
                    case 'resumePlayer':
                        console.log('Received resumePlayer state');
                        setResumedPlayerState(message.payload?.state || null);
                        if (message.payload?.state) {
                            const st = message.payload.state;
                            if (typeof st.score === 'number') setMyScore(st.score);
                            if (typeof st.glowLevel === 'number') setMyGlow({ level: st.glowLevel || 0, updatedMs: st.glowUpdatedMs || Date.now() });
                        }
                        break;
                    case 'diamondsSnapshot': {
                        const list = message.payload?.diamonds || [];
                        const byId = {};
                        list.forEach(d => { if (d && d.id != null) byId[d.id] = d; });
                        setDiamonds(byId);
                        break;
                    }
                    case 'diamondsUpdate': {
                        setDiamonds(prev => {
                            const updated = { ...prev };
                            const removed = message.payload?.removed || [];
                            removed.forEach(id => { delete updated[id]; });
                            const added = message.payload?.added || [];
                            added.forEach(d => { if (d && d.id != null) updated[d.id] = d; });
                            return updated;
                        });
                        break;
                    }
                    case 'diamondCollected': {
                        const dId = message.payload?.id;
                        const pos = message.payload?.position;
                        const collector = message.payload?.pUUID;
                        if (dId != null && Array.isArray(pos) && pos.length >= 3 && collector) {
                            // Create a new effect instance
                            const effect = {
                                id: `${dId}-${Date.now()}`,
                                diamondId: dId,
                                from: [pos[0], pos[1], pos[2]],
                                toPUUID: collector,
                                started: Date.now(),
                                duration: 800, // ms
                                color: '#00ffff',
                            };
                            setPickupEffects(prev => [...prev, effect]);
                        }
                        break;
                    }
                    // Add more cases for other message types...
                    default:
                        console.warn('Received unknown message type:', message.type);
                }

            } catch (error) {
                console.error('Failed to parse message or invalid message format:', event.data, error);
            }
        };
    }, [pUUID]); // include pUUID so hello has correct id

    // Effect to connect on mount and disconnect on unmount
    useEffect(() => {
        connectWebSocket(); // Connect when provider mounts

        return () => {
            // Cleanup on unmount
            if (ws.current) {
                console.log('Closing WebSocket connection on provider unmount.');
                ws.current.close();
                ws.current = null;
            }
        };
    }, [connectWebSocket]);

    // Function to send data (stringifies JSON)
    const sendData = useCallback((data) => {
        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
            try {
                // Add player UUID to every message
                const messageWithUUID = {
                    ...data,
                    pUUID,
                    timestamp: Date.now()
                };

                const messageString = JSON.stringify(messageWithUUID);
                ws.current.send(messageString);
            } catch (error) {
                console.error("Failed to stringify or send data:", data, error);
            }
        } else {
            console.warn('WebSocket not connected. Cannot send data:', data);
        }
    }, [pUUID]);

    // Set up ping interval
    useEffect(() => {
        if (!isConnected) return;

        // Send ping every 5 seconds to measure lag
        const pingInterval = setInterval(() => {
            sendData({
                type: 'ping',
                timestamp: Date.now()
            });
        }, 5000);

        return () => clearInterval(pingInterval);
    }, [isConnected, sendData]);

    // Add a cleanup function for player departure
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (ws.current && ws.current.readyState === WebSocket.OPEN) {
                const msg = JSON.stringify({
                    type: 'playerLeaving',
                    pUUID: pUUID,
                    timestamp: Date.now()
                });
                ws.current.send(msg);
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [pUUID]);

    // Set up the logging interval with stable dependencies
    useEffect(() => {
        if (!isConnected) return;
        const statsLoggingInterval = setInterval(() => {
            const { avgPingTime, serverTimeOffset } = statsRef.current;
            const currentServerTime = Date.now() + serverTimeOffset;
            console.log(
                `WebSocket Stats - Avg Ping: ${Math.round(avgPingTime)}ms | ` +
                `Server Time Offset: ${Math.round(serverTimeOffset)}ms | ` +
                `Estimated Server Time: ${new Date(currentServerTime).toISOString()}`
            );
        }, 30000);

        return () => clearInterval(statsLoggingInterval);
    }, [isConnected]);

    // Helper to compute current glow with decay (mirror server)
    const computeCurrentGlow = useCallback(() => {
        const decayPerSecond = 1.0 / 300.0; // keep in sync with server
        const now = Date.now();
        const elapsedSec = Math.max(0, (now - (myGlow.updatedMs || now)) / 1000);
        const g = (myGlow.level || 0) - decayPerSecond * elapsedSec;
        return Math.max(0, g);
    }, [myGlow]);

    // Helper to remove a pickup effect by id
    const removePickupEffect = useCallback((id) => {
        setPickupEffects(prev => prev.filter(e => e.id !== id));
    }, []);

    const value = {
        pUUID,
        isConnected,
        worldState,
        resumedPlayerState, // NEW
        diamonds, // NEW
        pickupEffects, // NEW
        removePickupEffect, // NEW
        myScore, // NEW
        myGlow, // NEW
        computeCurrentGlow, // NEW
        sendData,
        ws,
        serverTimeOffset,
        avgPingTime,
        getServerTime,
    };

    return (
        <WebSocketContext.Provider value={value}>
            {children}
        </WebSocketContext.Provider>
    );
};
